import { Injectable } from '@angular/core';
import { AppService } from '../../services/app.service';
import { PermissionService } from '../../services/permission.service';
import { ProductsCategoryService } from '../../services/products-category.service';
import { Product } from '../../services/products.service';
import { QuoteDiscount, QuoteDiscountsService } from '../../services/quote-discounts.service';
import { BehaviorSubject } from 'rxjs';
import {
    ConditionType,
    OfferCreationParams,
    OfferParams,
    OfferPriceCalculationParams,
    PriceLiteParams,
} from './price-summary.model';
import { Quote } from '../../services/quote.service';
import { EnvironmentDiffService } from '../../services/environment-diff.service';
import { OfferCondition, OfferItem, OfferPrintOptions } from '../../models/offer.model';
import { Application } from '../../models/application.model';

export interface ProductWithGroup extends Product {
    group?: string;
}

interface OrderTypeMapping {
    application: Application;
    orderType: {
        employee: string;
        customer: string;
    };
}

const orderTypeMappings: OrderTypeMapping[] = [
    {
        application: Application.quoteAssist,
        orderType: {
            employee: 'ZPPC',
            customer: 'ZPPB',
        },
    },
    {
        application: Application.ventilationPlanner,
        orderType: {
            employee: 'ZLPM',
            customer: 'ZLPK',
        },
    },
    {
        application: Application.pvPlanner,
        orderType: {
            employee: 'ZPVM',
            customer: 'ZPVK',
        },
    },
    {
        application: Application.vitoairProSelect,
        orderType: {
            employee: 'AHUM',
            customer: 'AHUK',
        },
    },
    {
        application: Application.a2aPlanner,
        orderType: {
            employee: 'ZAAM',
            customer: 'ZAAK',
        },
    },
];

@Injectable({
    providedIn: 'root',
})
export class PriceSummaryHelperService {
    public priceAdvantageEnabled$: BehaviorSubject<boolean> = new BehaviorSubject(true);

    constructor(
        private permissionService: PermissionService,
        private quoteDiscountsService: QuoteDiscountsService,
        private appService: AppService,
        private productCategory: ProductsCategoryService,
        private environmentDiffService: EnvironmentDiffService
    ) {}

    public getPriceLiteParams(products: Product[]): PriceLiteParams {
        const priceAdvantageEnabled = this.priceAdvantageEnabled$.value;
        return {
            salesOrg: this.appService.getSalesOrg(),
            language: this.appService.getLanguageOnly(),
            country: this.appService.getLocation(),
            materials: products
                .filter((p) => p.selected && !p.optional)
                .map((p) => ({ materialNumber: p.material, amount: p.quantity })),
            discounts: priceAdvantageEnabled
                ? this.productCategory.getProductDiscountCategoriesFromCache(products)
                : [],
            priceAdvantageEnabled,
        };
    }

    public getOfferPriceCalculationParams(
        customerNumber: string,
        voucherCode: string,
        productList: ProductWithGroup[],
        sampleOfferId: string,
        quote: Quote
    ): OfferPriceCalculationParams {
        return {
            language: this.appService.getLanguageOnly(),
            offer: this.getOfferParams(customerNumber, voucherCode, productList, sampleOfferId, quote),
        };
    }

    public getOfferCreationParams(
        customerNumber: string,
        voucherCode: string,
        productList: ProductWithGroup[],
        sampleOfferId: string,
        quote: Quote
    ): OfferCreationParams {
        return {
            ...this.getOfferParams(customerNumber, voucherCode, productList, sampleOfferId, quote),
            printOptions: this.getPrintOptions(quote),
            texts: this.getOfferTexts(),
        };
    }

    private getOfferTexts(): { [key: string]: string } {
        const texts = Object.entries({
            '0010': this.environmentDiffService.getHybridPackageFromCache(),
        }).filter(([_, value]) => !!value);
        return Object.fromEntries(texts);
    }

    private getOfferParams(
        customerNumber: string,
        voucherCode: string,
        productList: ProductWithGroup[],
        sampleOfferId: string,
        quote: Quote
    ): OfferParams {
        const salesOrg = this.appService.getSalesOrg();
        const schemaId = quote.schemes?.primary?.split(';')[0];

        const priceAdvantageEnabled = this.priceAdvantageEnabled$.value;
        let index = 1;
        const { categoryDiscounts, partnerDiscounts } = this.getDiscounts(productList);
        const items: OfferItem[] = productList
            .filter((product) => product.selected)
            .map((product) => {
                const partnerDiscount = partnerDiscounts.find(
                    (d) => d.materialGroup === product.materialGroup
                )?.partnerDiscount;
                const categoryDiscount =
                    product.materialGroup !== 'N' &&
                    categoryDiscounts.find((cd) => cd.category === product.category)?.discount;
                const conditions: OfferCondition[] = [
                    categoryDiscount && { type: ConditionType.standard, value: -categoryDiscount },
                    partnerDiscount && { type: ConditionType.partner, value: Number(partnerDiscount) },
                ].filter(Boolean);
                return {
                    itemNumber: index++,
                    material: product.material,
                    shortText: product.shortText,
                    quantity: product.quantity,
                    group: product.group,
                    optional: product.optional,
                    conditions,
                    printOptions: product.printOptions,
                };
            });
        const offerType = this.getOfferType(sampleOfferId, quote);
        return {
            salesOrg,
            distrChan: '01',
            division: '01',
            customerPurchaseNumber: quote.customerPurchaseNumber,
            documentType: 'ZPAG',
            customerNumber,
            template: sampleOfferId,
            submissionNumber: quote.submissionNumber,
            salesforcePlanningId: quote.salesforcePlanningId,
            salesforceOpportunityId: quote.salesforceOpportunityId,
            currency: quote.currency,
            schemaId,
            voucherId: voucherCode,
            offerType,
            quoteAssistInfo: priceAdvantageEnabled ? 'P' : '',
            items,
            printOptions: this.getPrintOptions(quote),
        };
    }

    public getOfferType(sampleOffer: string, quote: Quote) {
        if (quote.noPriceAdvantage) {
            return sampleOffer ? 'ZPPH' : 'ZPPE';
        }
        if (sampleOffer) {
            return this.permissionService.isUserAnEmployee ? 'ZPPG' : 'ZPPF';
        }

        const mapping = orderTypeMappings.find((m) => m.application === quote.type);
        const role = this.permissionService.userInfo$.value.role;
        return mapping.orderType[role];
    }

    private getPrintOptions(quote: Quote): OfferPrintOptions {
        return {
            printType: quote.printType || 'LONG',
            printLocale: this.appService.getLanguageAsLocale(),
            textMap: {},
            options: {
                draft: false,
                printItemPrices: true,
                printDiscounts: true,
                printLogo: true,
            },
        };
    }

    public getDiscounts(products: Product[]) {
        return {
            categoryDiscounts: this.priceAdvantageEnabled$.value
                ? this.productCategory.getProductDiscountCategoriesFromCache(products)
                : [],

            partnerDiscounts:
                this.permissionService.isUserAnEmployee && this.quoteDiscountsService.getQuoteDiscounts().length > 0
                    ? this.quoteDiscountsService.getQuoteDiscounts()
                    : [],
        };
    }

    public hasProductListChanged(array1: Array<any>, array2: Array<any>) {
        const arrayString1 = JSON.stringify(array1);
        const arrayString2 = JSON.stringify(array2);
        const result = arrayString1 !== arrayString2;
        return result;
    }

    public quoteDiscountsDiffer(prev: Array<QuoteDiscount>, current: Array<QuoteDiscount>): boolean {
        if (prev == null) {
            return true;
        }
        // 1. We convert each discount to a normalized string representation: GG_15
        // 2. we check the difference in both ways to determine if the arrays are equal
        const prevStrings = prev.map((it) => it.materialGroup + '_' + String(it.partnerDiscount));
        const currentStrings = current.map((it) => it.materialGroup + '_' + String(it.partnerDiscount));
        const diff1 = prevStrings.filter((it) => !currentStrings.includes(it));
        const diff2 = currentStrings.filter((it) => !prevStrings.includes(it));
        return diff1.length + diff2.length > 0;
    }
}
